import React from 'react';
import { Image } from 'antd';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';

// @components
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { CSTooltip } from 'components/Tooltip/CSToolTip';

// @hooks
import { useSeenOfferPixel } from 'hook/helper.hook';

// @helpers
import { isFn } from 'helpers/objectHelpers';
import { ConditionalRendering } from 'helpers/conditionalRedering.helper';
import { getSeenOfferActionTrackingAttributes } from 'helpers/tracking.helpers';

// @constants
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { NAVIGATION } from 'externals/_tracking/types/clickEventTypes.constants';

// @images
import { ReactComponent as ArrowUp } from 'style/_assets/icons/arrow-up-white.svg';
import { ReactComponent as ChevronRight } from 'style/_assets/icons/chevron-right.svg';

// @styles
import './GoalCard.scss';

// @config
import CardsConfig, {
  addScoreIncreaseSimulation,
} from 'goals_card_config/config';
import { getOpenAutoLoanAccounts } from 'helpers/credit_info';
import OText from 'components/CText/OText';
import { makeBoldPartOfTheText } from 'helpers/string.helper';

const CardTitleBuilder = ({
  cardTitle,
  scoreIncrease,
  config,
  autoRefinanceInfo,
  tooltip,
  hideTooltip,
}) => {
  let cardTooltip = tooltip;
  if (isFn(cardTooltip)) {
    cardTooltip = cardTooltip({
      config,
      autoRefinanceCurrentLoadDetails: autoRefinanceInfo?.currentLoanDetails,
    });
  }

  return (
    <FlexContainer
      alignItemsCenter
      className="primary_light padtb12 padlr16 borderTopRadius8"
    >
      {scoreIncrease}
      <span className="toUpper text-12 m-text-10 secondary_cta_text_light padt2">
        {cardTitle}
      </span>
      <ConditionalRendering isTrue={!hideTooltip}>
        <CSTooltip isInline className="tooltip" tooltipContent={cardTooltip} />
      </ConditionalRendering>
    </FlexContainer>
  );
};

const ScoreIncreaseMoneySavingSimulation = ({
  action,
  hasScoreIncreaseSimulation,
  scoreIncreaseSimulation,
  hasSavingsSimulation,
}) => {
  if (hasScoreIncreaseSimulation) {
    const scoreIncreaseValue = scoreIncreaseSimulation(
      action.simulationsResponse,
    );
    if (scoreIncreaseValue) {
      return (
        <div className="FlexContainer align-items-center flex-1">
          <div className="margTopN3">
            <ArrowUp role="img" aria-hidden="true" width={18}></ArrowUp>
          </div>
          <div className="margr4 m-text-14 secondary_cta_text_light">
            {scoreIncreaseValue} pts.{' '}
          </div>
        </div>
      );
    }
  }
  if (hasSavingsSimulation) {
    return (
      <div className="scoreIncreaseAutoLoan margr4 flex-1 secondary_cta_text_light">
        ${action?.potentialSavings}
        <span className="text-12 m-text-10 secondary_cta_text_light">/MO.</span>
      </div>
    );
  }

  return <></>;
};

const GoalCard = props => {
  const history = useHistory();
  const action = props.action;
  let autoRefinanceInfo = null;

  const autoLoansCount = getOpenAutoLoanAccounts(props?.creditInfo);

  if (
    props.creditRepairScoreSimWebEnabled &&
    action?.name === 'CREDIT_REPAIR_EXPERT'
  ) {
    CardsConfig[action?.name] = addScoreIncreaseSimulation(
      CardsConfig[action?.name],
    );
  }

  const {
    actionName,
    vertical,
    linkPath,
    image,
    cardTitle,
    partner,
    productKey,
    tooltip,
    hideTooltip,
    scoreIncreaseSimulation,
    hasScoreIncreaseSimulation,
    hasSavingsSimulation,
  } = CardsConfig[action?.name];

  let finalLinkPath = linkPath;

  if (action.name === 'AUTO_REFI') {
    autoRefinanceInfo = props?.autoRefinanceInfo();
    if (autoLoansCount?.length >= 2)
      finalLinkPath = '/offers?vertical=AUTO_LOAN';
  }

  const attributes = {
    action: `Action - ${actionName}`,
    offerModulePosition: props?.offerPosition,
    moduleName: props?.moduleName,
    modulePosition: props?.modulePosition,
    pageName: props?.pageName,
    vertical,
  };
  const moduleMetadata = action?.forceToUse
    ? {
        extraTileTrackingAttrs: {
          'CB RR Upsell Rules': action?.cbRRUpsellRules,
        },
      }
    : {};
  const trackingAttributes = getSeenOfferActionTrackingAttributes({
    ...attributes,
    moduleMetadata,
  });
  const clickEvent = {
    ...trackingAttributes,
    'Click Title': `Action - ${actionName}`,
    Partner: partner,
    'Product Key': productKey,
  };
  if (!action?.forceToUse) {
    delete clickEvent['Action Name'];
  }
  const seenOfferPixel = useSeenOfferPixel(trackingAttributes);

  const scoreIncrease = (
    <ScoreIncreaseMoneySavingSimulation
      action={action}
      allSimulationsTrackingAttrs={props?.allSimulationsTrackingAttrs}
      cardTitle={cardTitle}
      hasScoreIncreaseSimulation={hasScoreIncreaseSimulation}
      hasSavingsSimulation={hasSavingsSimulation}
      scoreIncreaseSimulation={scoreIncreaseSimulation}
    />
  );

  const handleActionCardClick = () => {
    props.eventHandler(CLICK, {
      'Click Type': NAVIGATION,
      ...clickEvent,
    });
    history.push(finalLinkPath);
  };

  const highlightNewCardScoreIncrease = actionCardText => {
    const regex = /(\d+\s*points)/g;
    return makeBoldPartOfTheText(regex, actionCardText);
  };

  const actionCardDescription =
    action?.name === 'NEW_CARD_SCORE_INCREASE' ||
    action?.name === 'NEW_CC_SCORE_OVER_TIME'
      ? highlightNewCardScoreIncrease(action?.actionCardText)
      : action?.actionCardText;

  return (
    <div>
      {seenOfferPixel}
      <FlexContainer
        onClick={handleActionCardClick}
        className="actionCardContainer fright grayShadow pointer"
        tabIndex={0}
        role="link"
      >
        <FlexContainer
          className={classnames(
            'grayShadow system_neutral_n000 height100 cardBorderRadiusTop',
          )}
          column
        >
          <CardTitleBuilder
            cardTitle={cardTitle}
            scoreIncrease={scoreIncrease}
            config={props?.config}
            autoRefinanceInfo={autoRefinanceInfo}
            tooltip={tooltip}
            hideTooltip={hideTooltip}
          />
          <FlexContainer
            alignItemsCenter
            className="pad16 column-gap-12 height100"
          >
            <div className="format30">
              <Image src={image?.src} alt="" width={85} preview={false} />
            </div>
            <span
              className={`system_neutral_n400_text ${
                props?.config?.phxHomepageActions
                  ? 'fontRobotoMedium'
                  : 'subTitle2 '
              }`}
            >
              <OText text={actionCardDescription} />
            </span>
            <div className="format16">
              <ChevronRight className="chevronIcon" />
            </div>
          </FlexContainer>
        </FlexContainer>
      </FlexContainer>
    </div>
  );
};

export default GoalCard;
